/**
 * Safe JSON and Date utilities for handling potentially corrupted data
 *
 * This module provides utilities to safely parse JSON and handle Date objects
 * that may be corrupted or invalid in local storage. Used by PreferenceStore,
 * SQLiteService, and DexieService to prevent app crashes from malformed data.
 */

export type SafeParseResult<T> =
  | { success: true; data: T; error: null }
  | { success: false; data: null; error: string };

/**
 * Safely parse JSON with detailed error information
 *
 * @param jsonString - The JSON string to parse
 * @param context - Optional context for error logging (e.g., "SQLite:getSystemConfig:app_preferences")
 * @returns SafeParseResult with either parsed data or error details
 *
 * @example
 * const result = safeJSONParse<Preferences>(jsonString, 'PreferenceStore');
 * if (result.success) {
 *   console.log(result.data);
 * } else {
 *   console.error(result.error);
 * }
 */
export function safeJSONParse<T = unknown>(
  jsonString: string,
  context?: string
): SafeParseResult<T> {
  try {
    // Handle edge cases
    if (jsonString === null || jsonString === undefined) {
      return {
        success: false,
        data: null,
        error: 'Cannot parse null or undefined',
      };
    }

    if (typeof jsonString !== 'string') {
      return {
        success: false,
        data: null,
        error: `Expected string, got ${typeof jsonString}`,
      };
    }

    if (jsonString.trim() === '') {
      return {
        success: false,
        data: null,
        error: 'Cannot parse empty string',
      };
    }

    const data = JSON.parse(jsonString) as T;
    return { success: true, data, error: null };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown parse error';
    const contextInfo = context ? ` [Context: ${context}]` : '';

    console.error(`JSON parse failed${contextInfo}:`, {
      error: errorMessage,
      input: jsonString?.substring(0, 100), // Log first 100 chars
      inputLength: jsonString?.length,
    });

    return {
      success: false,
      data: null,
      error: `JSON parse error: ${errorMessage}${contextInfo}`,
    };
  }
}

/**
 * Validates if a Date object is valid
 *
 * @param date - The value to check
 * @returns true if the value is a valid Date object
 *
 * @example
 * if (isValidDate(someValue)) {
 *   console.log(someValue.getTime());
 * }
 */
export function isValidDate(date: unknown): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Safely convert string/number/Date to Date, with validation
 *
 * @param value - The value to parse as a Date
 * @param fallback - Optional fallback Date to use if parsing fails (defaults to current date)
 * @returns A valid Date object
 *
 * @example
 * const date = safeParseDate('2024-01-01T00:00:00Z');
 * const dateWithFallback = safeParseDate('invalid', new Date('2024-01-01'));
 */
export function safeParseDate(
  value: unknown,
  fallback?: Date
): Date {
  // Already a valid Date
  if (isValidDate(value)) {
    return value;
  }

  // Try parsing string
  if (typeof value === 'string') {
    const date = new Date(value);
    if (isValidDate(date)) {
      return date;
    }
  }

  // Try parsing number (timestamp)
  if (typeof value === 'number') {
    const date = new Date(value);
    if (isValidDate(date)) {
      return date;
    }
  }

  // Fallback
  const result = fallback || new Date();
  console.warn('Invalid date value, using fallback:', {
    value,
    fallback: result,
  });
  return result;
}

/**
 * Safely call getTime() on potentially invalid dates
 *
 * @param date - The Date (or date-like value) to get time from
 * @returns Timestamp in milliseconds, or 0 if date is invalid
 *
 * @example
 * // Safe for sorting
 * amounts.sort((a, b) => safeGetTime(a.lastUsed) - safeGetTime(b.lastUsed));
 */
export function safeGetTime(date: unknown): number {
  if (isValidDate(date)) {
    return date.getTime();
  }
  console.warn('Called getTime() on invalid date:', date);
  return 0; // Epoch time for invalid dates
}
